import { useState, useEffect, useRef, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import '@/App.css';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import PanelOperador from './pages/PanelOperador';
import PanelGerencial from './pages/PanelGerencial';
import VistaCita from './pages/VistaCita';
import CambiarPassword from './pages/CambiarPassword';
import GoogleCallback from './pages/GoogleCallback';
import Formulario1 from './pages/Formulario1';
import Formulario2 from './pages/Formulario2';
import Formulario2Confirmado from './pages/Formulario2Confirmado';
import Formulario2Exitoso from './pages/Formulario2Exitoso';
import SubsanarDocumento from './pages/SubsanarDocumento';
import FirmarDeclaracion from './pages/FirmarDeclaracion';
import ExpedienteDetalle from './pages/ExpedienteDetalle';
import EmailTemplates from './pages/EmailTemplates';
import EmailTracking from './pages/EmailTracking';
import MonitoreoEmails from './pages/MonitoreoEmails';
import BlacklistCedulas from './pages/BlacklistCedulas';
import ConfiguracionSistema from './pages/ConfiguracionSistema';
import AgendarCita from './pages/AgendarCita';
import CitaConfirmada from './pages/CitaConfirmada';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import GestorCitas from './pages/GestorCitas';
import ErrorLogs from './pages/ErrorLogs';
import ClientesSinCita from './pages/ClientesSinCita';
import RegistroVIP from './pages/RegistroVIP';
import NotificacionesCompletas from './pages/NotificacionesCompletas';
import CargarDocumentos from './pages/CargarDocumentos';
import LoadingScreen from './components/LoadingScreen';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { logErrorToBackend } from './utils/errorLogger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// =============================================
// 1. ErrorBoundary global
// =============================================
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info?.componentStack);
    try {
      logErrorToBackend({
        tipo: 'react_error_boundary',
        endpoint: window.location.pathname,
        error: error?.message || String(error),
        datos: { componentStack: info?.componentStack || '' }
      });
    } catch (e) {
      // No romper el fallback si el logging falla
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div data-testid="error-boundary-view" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'linear-gradient(to bottom right, #111827, #1f2937, #000)', color: '#fff', fontFamily: 'system-ui, sans-serif', padding: '24px' }}>
          <div style={{ maxWidth: '460px', width: '100%' }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <img src="/kompa-logo.png" alt="Kompa" style={{ height: '56px', width: 'auto', margin: '0 auto' }} />
            </div>
            <div style={{ background: 'rgba(31,41,55,0.5)', border: '1px solid #374151', borderRadius: '12px', padding: '24px', backdropFilter: 'blur(12px)' }}>
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '0.78rem', fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>¿Qué pasó?</p>
                <p style={{ fontSize: '1rem', color: '#f3f4f6', margin: 0 }}>No pudimos mostrar esta sección correctamente.</p>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '0.78rem', fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>¿Por qué pasó?</p>
                <p style={{ fontSize: '0.92rem', color: '#9ca3af', margin: 0 }}>La página tuvo una interrupción temporal mientras se actualizaba en tu dispositivo.</p>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <p style={{ fontSize: '0.78rem', fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>¿Cómo se soluciona?</p>
                <p style={{ fontSize: '0.92rem', color: '#9ca3af', margin: 0 }}>Toca "Reintentar" para volver a cargar. Si el problema continúa, escríbenos a <a href="mailto:desactivate@kompa.global" style={{ color: '#f59e0b' }}>desactivate@kompa.global</a>.</p>
              </div>
              <button
                data-testid="error-boundary-retry-btn"
                onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
                style={{ width: '100%', padding: '12px 28px', background: '#eab308', color: '#000', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem' }}
              >
                Reintentar
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Componente helper para redirigir según rol
function RoleBasedRedirect({ user }) {
  if (!user) return <Navigate to="/" />;
  
  switch(user.role) {
    case 'operador':
      return <Navigate to="/operador/panel" replace />;
    case 'gerencial':
      return <Navigate to="/gerencial/panel" replace />;
    case 'admin':
      return <Navigate to="/dashboard" replace />;
    default:
      return <Navigate to="/dashboard" replace />;
  }
}

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const abortRef = useRef(null);

  // =============================================
  // 3. useEffect con cleanup de AbortController
  // =============================================
  useEffect(() => {
    const publicRoutes = ['/formulario-1', '/formulario-2', '/registro-vip', '/agendar-cita', 
                         '/cita-confirmada', '/subsanar-documento', '/firmar', 
                         '/formulario-2-confirmado', '/formulario-2-exitoso', '/cargar-documentos'];
    
    const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));
    
    if (isPublicRoute) {
      setLoading(false);
      setIsAuthenticated(false);
      return;
    }
    
    const hash = location.hash;
    if (hash && hash.includes('session_id=')) {
      handleSessionId(hash);
    } else {
      checkAuth();
    }

    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, []);

  const handleSessionId = async (hash) => {
    const sessionId = hash.split('session_id=')[1]?.split('&')[0];
    
    if (!sessionId) {
      checkAuth();
      return;
    }

    try {
      console.log('Processing session_id:', sessionId);
      const response = await fetch(`${BACKEND_URL}/api/auth/session?session_id=${sessionId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (response.ok && data.success) {
        // Redirigir según rol del usuario
        const userRole = data.user?.role || 'operador';
        let redirectPath = '/dashboard';
        
        if (userRole === 'operador') {
          redirectPath = '/operador/panel';
        } else if (userRole === 'gerencial') {
          redirectPath = '/gerencial/panel';
        }
        
        window.history.replaceState({}, document.title, redirectPath);
        setUser(data.user);
        setIsAuthenticated(true);
        setLoading(false);
        toast.success('¡Bienvenido a Kompa!');
        navigate(redirectPath, { replace: true });
      } else {
        console.error('Auth failed:', data);
        toast.error('Error de autenticación');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al iniciar sesión');
      setLoading(false);
    }
  };

  const checkAuth = async () => {
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include',
        signal: controller.signal
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem('kompa_user', JSON.stringify(userData));
      } else {
        setIsAuthenticated(false);
        localStorage.removeItem('kompa_user');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
      }
    } finally {
      abortRef.current = null;
      setLoading(false);
    }
  };

  // =============================================
  // 2. Loading como overlay (no reemplaza el DOM)
  // =============================================
  return (
    <div key="app-root-stable" style={{ position: 'relative', minHeight: '100vh' }}>
      {loading && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>
          <LoadingScreen message="Inicializando sistema..." timeout={8000} />
        </div>
      )}
      <div style={{ visibility: loading ? 'hidden' : 'visible' }}>
    <Routes>
      <Route 
        path="/" 
        element={
          isAuthenticated ? (
            <RoleBasedRedirect user={user} />
          ) : (
            <LoginPage onLogin={() => setIsAuthenticated(true)} />
          )
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          isAuthenticated ? (
            user?.role === 'operador' ? (
              <Navigate to="/operador/panel" />
            ) : user?.role === 'gerencial' ? (
              <Navigate to="/gerencial/panel" />
            ) : (
              <Dashboard user={user} onLogout={() => { localStorage.removeItem('kompa_user'); setIsAuthenticated(false); }} />
            )
          ) : (
            <Navigate to="/" />
          )
        } 
      />
      <Route path="/operador/panel" element={isAuthenticated && user?.role === 'operador' ? <PanelOperador user={user} onLogout={() => { localStorage.removeItem('kompa_user'); setIsAuthenticated(false); }} /> : <Navigate to="/" />} />
      <Route path="/operador/cita/:expediente_id" element={isAuthenticated && user?.role === 'operador' ? <VistaCita user={user} /> : <Navigate to="/" />} />
      <Route path="/gerencial/panel" element={isAuthenticated && user?.role === 'gerencial' ? <PanelGerencial user={user} /> : <Navigate to="/" />} />
      <Route path="/gerente/notificaciones" element={isAuthenticated && user?.role === 'gerencial' ? <NotificacionesCompletas /> : <Navigate to="/" />} />
      <Route path="/cambiar-password" element={isAuthenticated ? <CambiarPassword user={user} /> : <Navigate to="/" />} />
      <Route path="/email-tracking" element={isAuthenticated && user?.role === 'admin' ? <EmailTracking /> : <Navigate to="/" />} />
      <Route path="/google-callback" element={<GoogleCallback />} />
      <Route path="/formulario-1" element={<Formulario1 />} />
      <Route path="/formulario-2/:expediente_id" element={<Formulario2 />} />
      <Route path="/formulario-2" element={<Formulario2 />} />
      <Route path="/formulario-2-confirmado" element={<Formulario2Confirmado />} />
      <Route path="/formulario-2-exitoso" element={<Formulario2Exitoso />} />
      <Route path="/subsanar-documento/:token" element={<SubsanarDocumento />} />
      <Route path="/firmar/:expediente_id" element={<FirmarDeclaracion />} />
      <Route path="/firmar" element={<FirmarDeclaracion />} />
      <Route path="/registro-vip" element={<RegistroVIP />} />
      <Route path="/agendar-cita/:expediente_id" element={<AgendarCita />} />
      <Route path="/agendar-cita" element={<AgendarCita />} />
      <Route path="/cargar-documentos" element={<CargarDocumentos />} />
      <Route path="/cita-confirmada" element={<CitaConfirmada />} />
      <Route path="/expediente/:id" element={isAuthenticated ? <ExpedienteDetalle /> : <Navigate to="/" />} />
      <Route path="/email-templates" element={isAuthenticated && user?.role === 'admin' ? <EmailTemplates user={user} /> : <Navigate to="/" />} />
      <Route path="/email-tracking" element={isAuthenticated && user?.role === 'admin' ? <EmailTracking /> : <Navigate to="/" />} />
      <Route path="/monitoreo-emails" element={isAuthenticated && user?.role === 'admin' ? <MonitoreoEmails /> : <Navigate to="/" />} />
      <Route path="/blacklist-cedulas" element={isAuthenticated && user?.role === 'admin' ? <BlacklistCedulas /> : <Navigate to="/" />} />
      <Route path="/configuracion" element={isAuthenticated && user?.role === 'admin' ? <ConfiguracionSistema /> : <Navigate to="/" />} />
      <Route path="/analytics" element={isAuthenticated && (user?.role === 'admin' || user?.role === 'gerencial') ? <AnalyticsDashboard /> : <Navigate to="/" />} />
      <Route path="/gestor-citas" element={isAuthenticated && (user?.role === 'admin' || user?.role === 'gerencial') ? <GestorCitas /> : <Navigate to="/" />} />
      <Route path="/admin/error-logs" element={isAuthenticated && (user?.role === 'admin' || user?.role === 'gerencial') ? <ErrorLogs /> : <Navigate to="/" />} />
      <Route path="/admin/clientes-sin-cita" element={isAuthenticated && (user?.role === 'admin' || user?.role === 'gerencial') ? <ClientesSinCita /> : <Navigate to="/" />} />
    </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </ErrorBoundary>
  );
}

export default App;